import { ApiService } from "./api.service";
import { API_BASE_URL } from "@/config";
import type {
  FieldListResponse,
  Report,
  ReportCreateBody,
  ReportLookupItem,
  ReportObjectType,
  ReportRun,
  ReportsPage,
  ReportSnapshotResponse,
  ReportUpdateBody,
  SnapshotParams,
} from "@/types/report";

/**
 * API client for the Reports feature (Insights → Reports). Mirrors the backend
 * contract in docs/superpowers/specs/2026-06-24-reporting-design.md §4.1.
 * Reports are private to their owner (server-side scoped); this client only
 * speaks the wire shape.
 *
 * The run / snapshot / export methods (the async-snapshot run model) are
 * consumed by the Report View (issue 1678).
 */
export class ReportService extends ApiService {
  /** List the requesting user's own reports (paginated cursor envelope). */
  async list(slug: string): Promise<ReportsPage> {
    return this.get(`/api/workspaces/${slug}/reports/`);
  }

  async retrieve(slug: string, reportId: string): Promise<Report> {
    return this.get(`/api/workspaces/${slug}/reports/${reportId}/`);
  }

  async create(slug: string, body: ReportCreateBody): Promise<Report> {
    return this.post(`/api/workspaces/${slug}/reports/`, body);
  }

  async update(slug: string, reportId: string, body: ReportUpdateBody): Promise<Report> {
    return this.patch(`/api/workspaces/${slug}/reports/${reportId}/`, body);
  }

  async deleteReport(slug: string, reportId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/reports/${reportId}/`);
  }

  async duplicate(slug: string, reportId: string): Promise<Report> {
    return this.post(`/api/workspaces/${slug}/reports/${reportId}/duplicate/`);
  }

  /**
   * Fetch the field catalog projected for the builder. `objectTypes` drives the
   * object-column set + order; empty → shared + custom only.
   */
  async fetchFields(slug: string, objectTypes: ReportObjectType[]): Promise<FieldListResponse> {
    const params = new URLSearchParams();
    if (objectTypes.length > 0) params.set("object_types", objectTypes.join(","));
    return this.get(`/api/workspaces/${slug}/reports/fields/`, { params });
  }

  /**
   * Full `{ id, name }` set of projects the caller can see — backs the builder's
   * project relation-filter picker (#3504). A dedicated lookup (not the
   * cursor-paginated projects list) so the picker never omits a project past
   * page one.
   */
  async listProjectLookups(slug: string): Promise<ReportLookupItem[]> {
    return this.get(`/api/workspaces/${slug}/reports/lookups/projects/`);
  }

  /** Full `{ id, name }` set of requests the caller can see — the request-picker
   *  sibling of {@link listProjectLookups} (#3504). */
  async listRequestLookups(slug: string): Promise<ReportLookupItem[]> {
    return this.get(`/api/workspaces/${slug}/reports/lookups/requests/`);
  }

  /**
   * Trigger a run for a report. Returns the `ReportRun` — `complete` (with
   * resolved columns) when the run finishes inline, or `pending`/`running` when
   * it is queued async (past the row threshold). The caller then reloads the
   * snapshot (polling while the run is in flight).
   */
  async runReport(slug: string, reportId: string): Promise<ReportRun> {
    return this.post(`/api/workspaces/${slug}/reports/${reportId}/run/`);
  }

  /**
   * Fetch a page of the latest run's snapshot. Server-driven sort + pagination:
   * `sort`/`order` sort the whole snapshot, `cursor`/`perPage` page through it.
   * 404s before any run has been triggered (the "never run" state) — the caller
   * branches on that to show the run prompt.
   */
  async fetchSnapshot(
    slug: string,
    reportId: string,
    { cursor, perPage, sort, order }: SnapshotParams = {},
  ): Promise<ReportSnapshotResponse> {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    if (perPage != null) params.set("per_page", String(perPage));
    if (sort) params.set("sort", sort);
    if (order) params.set("order", order);
    return this.get(`/api/workspaces/${slug}/reports/${reportId}/snapshot/`, { params });
  }

  /**
   * Absolute URL for the CSV export of a report's latest run, with the active
   * sort applied. The export is a same-origin GET (`200 text/csv`); the Report
   * View triggers a browser download by navigating an anchor to this URL, so the
   * session cookie authenticates the request (the same cookie-auth navigation
   * the auth service uses for its form posts).
   */
  exportUrl(slug: string, reportId: string, { sort, order }: SnapshotParams = {}): string {
    const params = new URLSearchParams();
    if (sort) params.set("sort", sort);
    if (order) params.set("order", order);
    const query = params.toString();
    return `${API_BASE_URL}/api/workspaces/${slug}/reports/${reportId}/export/${query ? `?${query}` : ""}`;
  }

  /**
   * Enqueue an async CSV export of the latest run's snapshot on the worker, with
   * the active sort applied. `POST .../export/?sort=&order=` answers `202` with a
   * poll `token`; delivery is the in-app "export ready" notification the worker
   * fires on completion (carrying the presigned download URL). The Report View
   * takes this path for large reports (row count past the inline threshold) so a
   * big CSV is built off-request instead of streamed inline. 404s with the same
   * message as the sync GET if the report has no completed run.
   */
  async requestExport(
    slug: string,
    reportId: string,
    { sort, order }: SnapshotParams = {},
  ): Promise<{ token: string }> {
    const params = new URLSearchParams();
    if (sort) params.set("sort", sort);
    if (order) params.set("order", order);
    const query = params.toString();
    return this.post(
      `/api/workspaces/${slug}/reports/${reportId}/export/${query ? `?${query}` : ""}`,
    );
  }
}
